import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, UserPlus, UserMinus, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganisation } from '@/contexts/OrganisationContext';
import { replaceAgentUserAssignments } from '@/lib/api/agentAssignments';

interface AgentAssignmentManagerProps {
  companyId: string;
}

interface Agent {
  id: string;
  name: string;
  type: string;
  assign_to: string | null;
}

interface TeamMember {
  id: string;
  display_name: string;
  email: string;
}

export function AgentAssignmentManager({ companyId }: AgentAssignmentManagerProps) {
  const { user } = useAuth();
  const { hasEnterpriseHierarchy } = useOrganisation();
  const queryClient = useQueryClient();
  const [expandedAgentId, setExpandedAgentId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch company agents with specific assignment
  const { data: specificAgents = [] } = useQuery({
    queryKey: ['specific-agents', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('simulation_agents')
        .select('id, name, type, assign_to')
        .eq('company_id', companyId)
        .eq('assign_to', 'specific')
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      return data as Agent[];
    },
    enabled: !!companyId,
  });

  // Fetch team members
  const { data: teamMembers = [] } = useQuery({
    queryKey: ['team-members-list', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name, email')
        .eq('company_id', companyId)
        .neq('sso_provider', 'pending')
        .order('display_name');

      if (error) throw error;
      return data as TeamMember[];
    },
    enabled: !!companyId,
  });

  // Fetch current assignments
  const { data: assignments = [], refetch: refetchAssignments } = useQuery({
    queryKey: ['agent-assignments', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('agent_assignments')
        .select('id, agent_id, user_id')
        .in('agent_id', specificAgents.map(a => a.id));

      if (error) throw error;
      return data || [];
    },
    enabled: specificAgents.length > 0,
  });

  // Create assignment mutation
  const assignmentMutation = useMutation({
    mutationFn: async ({ agentId, userId }: { agentId: string; userId: string }) => {
      const currentUserIds = assignments
        .filter((assignment) => assignment.agent_id === agentId)
        .map((assignment) => assignment.user_id);
      const nextUserIds = currentUserIds.includes(userId)
        ? currentUserIds.filter((currentUserId) => currentUserId !== userId)
        : [...currentUserIds, userId];

      await replaceAgentUserAssignments({
        agentId,
        assignToAll: false,
        userIds: nextUserIds,
        assignedByProfileId: user?.id || null,
        useEnterpriseHierarchy: hasEnterpriseHierarchy,
      });
    },
    onSuccess: () => {
      refetchAssignments();
      queryClient.invalidateQueries({ queryKey: ['agent-assignments'] });
      toast.success('Agent access updated');
    },
    onError: (error: any) => {
      toast.error(`Failed to update access: ${error.message}`);
    },
  });

  const isUserAssigned = (agentId: string, userId: string) => {
    return assignments.some(a => a.agent_id === agentId && a.user_id === userId);
  };

  const getAssignedCount = (agentId: string) => {
    return assignments.filter(a => a.agent_id === agentId).length;
  };

  const toggleAssignment = (agentId: string, userId: string) => {
    assignmentMutation.mutate({ agentId, userId });
  };

  const filteredMembers = teamMembers.filter(member =>
    member.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (specificAgents.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Agent Assignments
        </CardTitle>
        <CardDescription>
          Manage which team members have access to agents with specific assignment
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {specificAgents.map((agent) => (
          <Collapsible
            key={agent.id}
            open={expandedAgentId === agent.id}
            onOpenChange={(open) => setExpandedAgentId(open ? agent.id : null)}
          >
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-card hover:bg-accent/50 cursor-pointer transition-colors">
                <div className="flex items-center gap-3">
                  <div>
                    <p className="font-medium text-sm">{agent.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{agent.type} Agent</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">
                    {getAssignedCount(agent.id)} / {teamMembers.length} assigned
                  </Badge>
                  {expandedAgentId === agent.id ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <div className="border rounded-lg p-4 space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search team members..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {filteredMembers.map((member) => {
                    const assigned = isUserAssigned(agent.id, member.id);
                    return (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-2 rounded hover:bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={assigned}
                            onCheckedChange={() => toggleAssignment(agent.id, member.id)}
                          />
                          <div>
                            <p className="text-sm font-medium">{member.display_name}</p>
                            <p className="text-xs text-muted-foreground">{member.email}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleAssignment(agent.id, member.id)}
                        >
                          {assigned ? (
                            <UserMinus className="h-4 w-4 text-destructive" />
                          ) : (
                            <UserPlus className="h-4 w-4 text-primary" />
                          )}
                        </Button>
                      </div>
                    );
                  })}
                  {filteredMembers.length === 0 && (
                    <p className="text-center text-sm text-muted-foreground py-4">
                      No team members found
                    </p>
                  )}
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        ))}
      </CardContent>
    </Card>
  );
}
